import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  DialogTitle,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { createEntryForm } from '../types';
import SelectField from 'components/select-field';
import { AccountingType } from 'v2-types/order';
import { TFunction } from 'i18next';
import TableCharges from './tableChargesComponents/tableCharges';
import TextField from 'components/text-field';
import DatePicker from 'components/date-picker/date-picker';
import { zodResolver } from '@hookform/resolvers/zod';
import { toISO8601LocalString } from 'utils/helpers';
import { schemaModalAddEntry } from '../services';
import { canLinkEntry } from '../utils/entry-eligibility';

type ModalAddEntryType = {
  accounting: AccountingType[];
  handleClose: () => void;
  t: TFunction;
  kindEntry: string[]; // Accept full array from disbursments.tsx (was single string)
  handleCreationEntry: (data: any) => void;
};

const useStyles = makeStyles((theme: any) => ({
  containerEmpty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing(3)} 0`,
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  actions: {
    padding: theme.spacing(1.5, 3, 2),
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5, 2),
      '& .MuiButton-root': {
        flex: 1,
        minHeight: theme.spacing(5.5),
      },
    },
  },
  actionButton: {
    textTransform: 'none',
  },
}));

const FORM_ID = 'modal-add-entry-form';

export const ModalAddEntry: FC<ModalAddEntryType> = ({
  handleClose,
  accounting,
  t,
  kindEntry,
  handleCreationEntry,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [filterText, setFilterText] = useState('');
  const [targetValidationError, setTargetValidationError] = useState('');
  const [filterFields, setFilterFields] = useState({ description: true, name: true, role: true });

  // Derive scalar kind for comparisons and form defaults — first element is always present
  const kind = kindEntry[0] as 'disbursement' | 'receipt' | 'netfunded';

  const allowedAssociations = [
    {
      code: 'cash',
      description: t('accounting:cash'),
    },
    {
      code: 'check',
      description: t('accounting:check'),
    },
    {
      code: 'wire',
      description: t('accounting:wire'),
    },
  ];

  const postedOptions = [
    {
      value: true,
      description: t('accounting:true'),
    },
    {
      value: false,
      description: t('accounting:false'),
    },
  ];

  const defaultCode = kind === 'netfunded' ? 'netfunded' : 'cash';

  const methods = useForm<createEntryForm>({
    resolver: zodResolver(schemaModalAddEntry),
    defaultValues: {
      code: defaultCode,
      targets: [],
      description: '',
      entryDate: toISO8601LocalString(new Date()) || '',
      kind,
      posted: true,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    trigger,
    setValue,
    clearErrors
  } = methods;

  // Clear the submission-validation error when the filter changes, but keep the
  // user's checked targets — TableCharges keeps selected rows visible across filters.
  const handleFilterTextChange = (text: string) => {
    setFilterText(text);
    setTargetValidationError('');
  };

  // Clear free-text search when all checkbox filters are unchecked.
  const toggleFilterField = (field: keyof typeof filterFields) => {
    const next = { ...filterFields, [field]: !filterFields[field] };
    setFilterFields(next);
    if (!next.description && !next.name && !next.role) {
      handleFilterTextChange('');
    }
  };

  const handleSubmitForm = handleSubmit((data) => {
    setTargetValidationError('');

    // Validate letter+number + J exclusion before posting — iterate targets, find each entry from accounting array
    const hasError = data.targets.some((target) => {
      const entry = accounting.find((e) => e._id === target.target_entry_id);
      if (!canLinkEntry(entry, kindEntry)) {
        setTargetValidationError(t('disbursments:error-missing-letter-number'));
        return true; // Prevent submission — guard runs before handleCreationEntry()
      }
      if (entry?.letter === 'J') {
        setTargetValidationError(t('disbursments:error-letter-j'));
        return true; // Prevent submission
      }
      return false;
    });
    if (hasError) return;

    const { code, ...otherprops } = data;
    handleCreationEntry((code as any) === "" ? { ...otherprops } : {
      ...data,
      targets: data.targets.map(f => {
        const { id, ...rest } = f as any;
        return {
          ...rest
        };
      })
    });
    handleClose();
  });

  const filteredEntries = accounting;

  const dialogTitle = (() => {
    if (kind === 'disbursement') return t('disbursments:createLinkEntry-disbursement');
    if (kind === 'receipt') return t('disbursments:createLinkEntry-receipt');
    if (kind === 'netfunded') return t('disbursments:createLinkEntry-netfunded');
    return t('disbursments:createLinkEntry');
  })();

  return (
    <Dialog open fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
          {t('disbursments:filter-helper-text')}
        </Typography>
        <Box className={classes.filterRow}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={filterFields.description}
                onChange={() => toggleFilterField('description')}
              />
            )}
            label={t('disbursments:filter-field-description')}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={filterFields.name}
                onChange={() => toggleFilterField('name')}
              />
            )}
            label={t('disbursments:filter-field-name')}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={filterFields.role}
                onChange={() => toggleFilterField('role')}
              />
            )}
            label={t('disbursments:filter-field-role')}
          />
        </Box>
        {!(
          !filterFields.description &&
          !filterFields.name &&
          !filterFields.role
        ) ? (
          <TextField
            value={filterText}
            onChange={(e) => handleFilterTextChange(e.target.value)}
            label={t('disbursments:filter')}
            fullWidth
          />
          ) : (
            <Typography variant="body2" color="textSecondary">
              {t('disbursments:filter-field-disabled')}
            </Typography>
          )}
        <form id={FORM_ID} onSubmit={handleSubmitForm}>
          {filteredEntries.length > 0 ? (
            <TableCharges
              accounting={filteredEntries}
              methods={methods}
              kindEntry={kindEntry} // NEW — needed for NetFunded party filtering
              filterText={filterText} // NEW — replaces selectedPayee
              filterFields={filterFields}
              t={t}
            />
          ) : (
            <Box className={classes.containerEmpty}>
              <Typography variant="h6">{t('disbursments:empty-entry')}</Typography>
            </Box>
          )}

          {targetValidationError && (
            <Typography color="error" variant="body2">
              {targetValidationError}
            </Typography>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="description"
                render={({ field: { ref, ...field } }) => (
                  <TextField
                    {...field}
                    value={field.value}
                    inputRef={ref}
                    label={t('disbursments:description')}
                    handleBlur={() => {
                      trigger('description');
                    }}
                    error={!!errors.description}
                    helperText={
                      errors.description?.message
                        ? t(`disbursments:${errors.description.message}`)
                        : ''
                    }
                  />
                )}
              />
            </Grid>
            {kind !== 'netfunded' && (
              <Grid item xs={6} sm={2}>
                <Controller
                  control={control}
                  name="code"
                  render={({ field: { ref, ...field } }) => (
                    <SelectField
                      {...field}
                      emptyValue={false}
                      value={field.value || 'cash'}
                      inputRef={ref}
                      data={allowedAssociations}
                      dataKey="code"
                      dataValue="code"
                      dataText="description"
                      label={t('users:kind')}
                      handleBlur={() => {
                        trigger('code');
                      }}
                      handleChange={field.onChange}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={kind !== 'netfunded' ? 6 : 12} sm={kind !== 'netfunded' ? 2 : 3}>
              <Controller
                control={control}
                name="posted"
                render={({ field: { ref, ...field } }) => (
                  <SelectField
                    {...field}
                    emptyValue={false}
                    value={field.value || false}
                    inputRef={ref}
                    data={postedOptions}
                    dataKey="value"
                    dataValue="value"
                    dataText="description"
                    label={t('disbursments:posted')}
                    handleBlur={() => {
                      trigger('posted');
                    }}
                    handleChange={field.onChange}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={kind !== 'netfunded' ? 2 : 3}>
              <Controller
                name="entryDate"
                control={control}
                render={({ field: { ref, value, ...field } }) => (
                  <DatePicker
                    {...field}
                    inputRef={ref}
                    disableToolbar={false}
                    label={t('accounting:entry-date')}
                    value={value as string | Date}
                    handleBlur={() => {
                      trigger('entryDate');
                    }}
                    error={!!errors.entryDate}
                    helperText={
                      errors.entryDate?.message ? t(`disbursments:${errors.entryDate.message}`) : ''
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          color="primary"
          variant="outlined"
          onClick={() => handleClose()}
          disableElevation
          className={classes.actionButton}
        >
          <Typography variant="body2">{t('disbursments:close')}</Typography>
        </Button>
        <Button
          disabled={!isValid}
          color="secondary"
          variant="contained"
          type="submit"
          form={FORM_ID}
          disableElevation
          className={classes.actionButton}
        >
          <Typography variant="body2">{t('disbursments:add')}</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAddEntry;
